import { useMemo } from "react";
import { useSelector } from "react-redux";
import { useCheckoutStateSelector } from "@checkout/context";
import { cartUpdateSelectors, SeatSelectors } from "@checkout/states";
import type { FiatPrice } from "@hopper-b2b/types";
import { getTripTotalInRewards } from "../utils";
import { useRewardsErrorMessage } from "@/hooks/shared/useRewardsErrorMesages";
import { AccountSelectors, useGetPointsBalance } from "@/modules/account";
import {
  getAppliedWalletOffer,
  getPointsRedemptionMaximum,
  getPointsToCashError,
  getPointsToCashLoading,
  getRewardsAmountToApply,
  getRewardsAmountToApplyCashEquivalent,
  getRewardsSelectedState,
  getWalletCreditPayAmount,
  RewardsPaymentSelectors,
  RewardsSelectedOptionType,
} from "@/modules/checkout/states";
import {
  getBaseFareFixedRewardsPrice,
  getCfarPrice,
  getDisruptionPrice,
  getIsFixedPriceQuote,
  getPricing,
  getTripInsuranceFiatPrice,
  getTripTotalFromQuoteBreakdownWithProducts,
} from "@/modules/flight-checkout/selectors";
import { getRemainingBalanceAfterRewardsApplied } from "@/utilities";

export const useRewardsData = () => {
  const {
    getPointsBalance,
    clearPointsBalanceError,
    basePointsToCashMultiplier,
    pointsBalance,
    pointsBalanceError: error,
  } = useGetPointsBalance();
  const { getPointsBalanceError } = useRewardsErrorMessage();

  const pointsBalanceError = useMemo(
    () => getPointsBalanceError(error as any),
    [getPointsBalanceError, error]
  );

  const hasPointsBalance = useSelector(AccountSelectors.getHasPoints);
  const isRewardsLoading = useSelector(AccountSelectors.isRewardsLoading);
  const rewardsAccountId = useSelector(AccountSelectors.getRewardsAccountId);

  const cartUpdateCallState = useCheckoutStateSelector(
    cartUpdateSelectors.getCartUpdateCallState
  );
  const rewardsSelectedState = useCheckoutStateSelector(
    getRewardsSelectedState
  );
  const isRewardsSelected =
    rewardsSelectedState === RewardsSelectedOptionType.APPLY_POINTS;

  const additionalPointsPurchase = useCheckoutStateSelector(
    RewardsPaymentSelectors.getAdditionalPointsPurchase
  );
  const isFixedPrice = useCheckoutStateSelector(getIsFixedPriceQuote);
  // NOTE: for fixed-price quotes `tripTotal.accountSpecific` is empty (the
  // frontend points estimate is intentionally omitted). Use
  // `tripTotalInRewards` below for the trip total in points, never
  // `tripTotal.accountSpecific[rewardsAccountId]` directly.
  const tripTotal = useCheckoutStateSelector(
    getTripTotalFromQuoteBreakdownWithProducts
  );
  const tripPricing = useCheckoutStateSelector(getPricing);
  const baseFareFixedRewardsPrice = useCheckoutStateSelector(
    getBaseFareFixedRewardsPrice
  );
  const creditAmountToApply: FiatPrice = useCheckoutStateSelector(
    getWalletCreditPayAmount
  );
  // Only an APPLIED offer reduces the points that can be redeemed.
  const walletOffer = useCheckoutStateSelector(getAppliedWalletOffer);
  const rewardsAmountToApply = useCheckoutStateSelector(
    getRewardsAmountToApply
  );
  const rewardsAmountToApplyCashEquivalent = useCheckoutStateSelector(
    getRewardsAmountToApplyCashEquivalent
  );
  // Authoritative max redeemable points for the current quote.
  const pointsRedemptionMaximum = useCheckoutStateSelector(
    getPointsRedemptionMaximum
  );
  // pointsToCash/quote fetch error/loading state.
  const pointsToCashError = useCheckoutStateSelector(getPointsToCashError);
  const pointsToCashLoading = useCheckoutStateSelector(getPointsToCashLoading);

  // The flex trip total in points, from the backend `maximum` (each product at
  // its correct rate).
  const tripTotalInRewards = useMemo(
    () =>
      isRewardsLoading
        ? undefined
        : getTripTotalInRewards(pointsRedemptionMaximum),
    [isRewardsLoading, pointsRedemptionMaximum]
  );

  const selectedSeatsPrice = useCheckoutStateSelector(
    SeatSelectors.getSeatTotalFiatPricing
  );
  const tripInsurancePrice = useCheckoutStateSelector(
    getTripInsuranceFiatPrice
  );
  const cfarFiatPrice = useCheckoutStateSelector(getCfarPrice);
  const disruptionFiatPrice = useCheckoutStateSelector(getDisruptionPrice);

  const hasSeats = selectedSeatsPrice != null && selectedSeatsPrice.value > 0;
  const hasInsurance =
    tripInsurancePrice != null && tripInsurancePrice.value > 0;
  const hasAddons =
    (cfarFiatPrice != null && cfarFiatPrice?.fiat?.value > 0) ||
    (disruptionFiatPrice != null && disruptionFiatPrice?.fiat?.value > 0);

  const remainingBalance = useMemo(() => {
    return getRemainingBalanceAfterRewardsApplied(
      tripTotal,
      rewardsAmountToApply,
      rewardsAmountToApplyCashEquivalent
    );
  }, [tripTotal, rewardsAmountToApply, rewardsAmountToApplyCashEquivalent]);

  return {
    // Points balance data
    pointsBalance,
    // Surface the pointsToCash fetch as loading so the rewards UI (pill spinner)
    // and the max-points memo hold steady while brackets (re)load. The internal
    // `isRewardsLoading` above stays account-only so it does not null out the
    // trip total during a bracket refetch.
    isRewardsLoading: isRewardsLoading || pointsToCashLoading,
    pointsBalanceError,
    hasPointsBalance,
    basePointsToCashMultiplier,
    rewardsAccountId,

    // Checkout state
    cartUpdateCallState,
    rewardsSelectedState,
    isRewardsSelected,
    rewardsAmountToApply,

    // Pricing data
    isFixedPrice,
    tripTotal,
    tripTotalInRewards,
    baseFareFixedRewardsPrice,
    pointsRedemptionMaximum,
    pointsToCashError,
    additionalPointsPurchase,
    tripPricing,

    // Product breakdown
    tripInsurancePrice,
    hasSeats,
    hasInsurance,
    hasAddons,
    remainingBalance,

    // Wallet/offers
    creditAmountToApply,
    walletOffer,

    // Actions
    getPointsBalance,
    clearPointsBalanceError,
  };
};
